import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, MenuItem, Select, Stack, Typography } from "@mui/material";
import StyleIcon from "@mui/icons-material/Style";
import { DCSProvider } from "./Table/DCSContext";
import DocumentList from "./Table/Dashboard/DocumentList";
import PageHeader from "../../../../../components/PageHeader";
import { fetchPlantRecord } from "./Table/Dashboard/docListAPI";
import { Types } from "./DocReviewer";

/**
 * Standalone Document Register page — reached from the project hub's
 * "Document Register" tile. Previously nested inside DCS/EMS's Doc
 * Reviewer as a modal; self-provisions its own DCSContext so it doesn't
 * depend on being mounted under the /plant/:plantId/dcs route.
 */
export default function DocumentRegisterPage() {
  const { plantId } = useParams();
  // Set when the projects tree opened this page from a WBS row's
  // "Add Document" - the WBS to file a new document under, and the newest
  // revision it has (see openDocumentRegister in EmsProjectsTreeView).
  const [searchParams] = useSearchParams();
  const defaultProjectId = searchParams.get("projectId") || "";
  const defaultProjectName = searchParams.get("projectName") || "";
  const defaultRevision = searchParams.get("revision") || "";
  const [type, setType] = useState("all");
  const [plantName, setPlantName] = useState("");

  useEffect(() => {
    if (!plantId) {
      setPlantName("");
      return;
    }
    let cancelled = false;
    // The same record the table and the WBS picker read, so naming the
    // page costs no request of its own (see fetchPlantRecord).
    fetchPlantRecord(plantId)
      .then((plant) => {
        if (!cancelled) setPlantName(plant?.name || "");
      })
      .catch(() => {
        if (!cancelled) setPlantName("");
      });
    return () => {
      cancelled = true;
    };
  }, [plantId]);

  return (
    <DCSProvider plant type={type}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <PageHeader
          plant={{ id: plantId, name: plantName }}
          pageTitle="Document Register"
          moduleKey="docRegister"
        />

        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "center", flexWrap: "wrap", px: 2, pt: 2 }}
        >
          <StyleIcon color="secondary" sx={{ fontSize: 18 }} />
          <Typography variant="body2" color="primary" fontWeight={500}>
            Document Type:
          </Typography>
          <Select
            value={type}
            onChange={(e) => setType(e.target.value)}
            displayEmpty
            inputProps={{ "aria-label": "Select document type" }}
            sx={{
              width: 170,
              backgroundColor: "background.paper",
              height: 32,
              fontSize: "0.8125rem",
              "& .MuiSelect-select": { py: 0.75 },
            }}
          >
            <MenuItem value="all" sx={{ fontSize: "0.8125rem" }}>
              All Types
            </MenuItem>
            {Types.map((option) => (
              <MenuItem key={option} value={option} sx={{ fontSize: "0.8125rem" }}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        <Box sx={{ p: 2 }}>
          <DocumentList
            defaultProjectId={defaultProjectId}
            defaultProjectName={defaultProjectName}
            defaultRevision={defaultRevision}
          />
        </Box>
      </Box>
    </DCSProvider>
  );
}
